var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ComponentException } from '@inter-ikea-kompis/exceptions';
import { KeyboardTypeEnum } from '@inter-ikea-kompis/keyboard-manager';
import { Text } from '@inter-ikea-kompis/component-text';
import { Button as SkapaButton, IconButton as SkapaIconButton } from '@ingka/button-webc/passive.js';
import KeyboardKeyCodeEnum from '../../enums/KeyboardKeyCodeEnum.js';
import KeyboardLayout from '../../enums/KeyboardLayout.js';
import KeyboardButton from '../keyboard-button/KeyboardButton.js';
import KeyboardStyle from './KeyboardStyle.js';
/**
 * This component displays a virtual keyboard typically used for the IKEA Kiosk.
 * Several keyboard layouts are available to support different needs. No
 * language specific keyboard layouts are available.
 *
 * @event keyPressed Dispatched when a key is pressed. Backspace is dispatched
 * with keyCode = 'backspace'.
 * @event done When the Done button is clicked.
 */
export default class Keyboard extends KompisElement {
    constructor() {
        super(...arguments);
        // The keyboard layout.
        this.layout = KeyboardLayout.qwerty.name;
        // Type of keyboard.
        this.type = KeyboardTypeEnum.text;
        // Locale is used to determine the type of decimal symbol (comma or point) on the "decimal" keyboard layout.
        this.locale = null;
        // True if the keyboard is visible.
        this.visible = false;
        // Done button label.
        this.doneButtonLabel = null;
        // Override z-index of the Keyboard.
        this.zIndex = 501;
    }
    /**
     * Renders the component.
     *
     * @returns Template.
     */
    render() {
        return html `
			<style>
				${KeyboardStyle.getStyle({
            isVisible: this.visible,
            theme: this.theme,
            zIndex: this.zIndex
        })}
			</style>
			<kompis-text .theme="${this.theme}"></kompis-text>
			<div @mousedown="${this.onMouseDown}" class="keyboard skapa-focus-portal">
				${this.getKeyRows()}
			</div>
		`;
    }
    async connectedCallback() {
        this.customElements.define('skapa-button', SkapaButton);
        this.customElements.define('skapa-icon-button', SkapaIconButton);
        this.customElements.define('kompis-text', Text);
        this.customElements.define('kompis-keyboard-button', KeyboardButton);
        super.connectedCallback();
    }
    /**
     * @param event Event.
     * Handles mouse down.
     */
    onMouseDown(event) {
        event.preventDefault();
    }
    /**
     * Returns HTML for the keys in a row.
     *
     * @returns HTML for the keys in a row.
     */
    getKeyRows() {
        const rows = KeyboardLayout[this.layout][this.type];
        return rows.map((row, index) => html `
				<div class="row">
					${row.map((keyCode) => this.getKeyboardButton(keyCode))}
					${index === rows.length - 1 ? this.getDoneButton() : null}
				</div>
			`);
    }
    /**
     * Returns HTML for a key.
     *
     * @param keyCode Key code.
     * @returns HTML for a key.
     */
    getKeyboardButton(keyCode) {
        return html `
			<kompis-keyboard-button
				@click="${this.onKeyPressed.bind(this, keyCode)}"
				.theme="${this.theme}"
				.keyCode="${keyCode}"
				.locale="${this.locale}"
				.type="${this.type}"
			></kompis-keyboard-button>
		`;
    }
    /**
     * Returns a function that emits keyPressed event.
     *
     * @param keyCode Key code.
     */
    onKeyPressed(keyCode) {
        this.dispatchEvent(new CustomEvent('keyPressed', {
            bubbles: true,
            detail: {
                keyCode: keyCode === KeyboardKeyCodeEnum.decimal ? this.getDecimalSymbol() : keyCode
            }
        }));
    }
    /**
     * @returns Close button.
     */
    getDoneButton() {
        const primaryVariant = 'primary';
        return html `
			<skapa-button variant="${primaryVariant}" @click="${this.onDoneClick.bind(this)}">
				${this.doneButtonLabel}
			</skapa-button>
		`;
    }
    /**
     * On close click event.
     */
    onDoneClick() {
        this.dispatchEvent(new CustomEvent('done', {
            bubbles: true
        }));
    }
    /**
     * Returns the decimal key text.
     *
     * @returns Key text.
     */
    getDecimalSymbol() {
        if (!this.locale) {
            throw new ComponentException('Failed get decimal symbol. The prop "locale" has not been defined.');
        }
        return '0' + (1.1).toLocaleString(this.locale).includes(',') ? ',' : '.';
    }
}
__decorate([
    property({ type: String })
], Keyboard.prototype, "layout", void 0);
__decorate([
    property({ type: String })
], Keyboard.prototype, "type", void 0);
__decorate([
    property({ type: String })
], Keyboard.prototype, "locale", void 0);
__decorate([
    property({ type: Boolean })
], Keyboard.prototype, "visible", void 0);
__decorate([
    property({ type: Object, required: true })
], Keyboard.prototype, "theme", void 0);
__decorate([
    property({ type: String })
], Keyboard.prototype, "doneButtonLabel", void 0);
__decorate([
    property({ type: Number })
], Keyboard.prototype, "zIndex", void 0);
//# sourceMappingURL=Keyboard.js.map